import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { apiService, ImportJob, ImportJobOutputsPage, ImportJobFilesPage } from '../services/api';
import ProcessingProgressBar from './ProcessingProgressBar';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import './JobCardCompact.css';

interface ImportJobCardCompactProps {
  job: ImportJob;
  isExpanded?: boolean;
  onToggleExpand?: (jobId: string) => void;
}

const getStatusTone = (status?: string): 'success' | 'warning' | 'danger' | 'muted' => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'failed' || normalized === 'cancelled') return 'danger';
  if (normalized === 'importing' || normalized === 'scanning' || normalized === 'pending') return 'warning';
  return 'muted';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTimeFull = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const ImportJobCardCompact: React.FC<ImportJobCardCompactProps> = ({
  job,
  isExpanded: controlledExpanded,
  onToggleExpand,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [outputPages, setOutputPages] = useState<ImportJobOutputsPage[]>([]);
  const [outputError, setOutputError] = useState<string | null>(null);
  const [loadingOutputs, setLoadingOutputs] = useState(false);
  const nextOutputCursor = outputPages.length ? outputPages[outputPages.length - 1].next_cursor : job.outputs?.next_cursor;
  const loadMoreOutputs = async () => {
    if (!nextOutputCursor) return;
    setLoadingOutputs(true);
    try {
      const page = await apiService.getImportJobOutputs(job.job_id, 100, nextOutputCursor);
      setOutputPages((previous) => [...previous, page]);
      setOutputError(null);
    } catch { setOutputError('Could not load more parts. Try again.'); }
    finally { setLoadingOutputs(false); }
  };
  const [failurePages, setFailurePages] = useState<ImportJobFilesPage[]>([]);
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [failureError, setFailureError] = useState<string | null>(null);
  const nextFailureCursor = failurePages.length ? failurePages[failurePages.length - 1].next_cursor : job.failed_files_next_cursor;
  const loadMoreFailures = async () => {
    if (!nextFailureCursor) return;
    setLoadingFailures(true);
    try {
      const page = await apiService.getImportJobFiles(job.job_id, 100, nextFailureCursor, true);
      setFailurePages((previous) => [...previous, page]);
      setFailureError(null);
    } catch { setFailureError('Could not load more failed files. Try again.'); }
    finally { setLoadingFailures(false); }
  };
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand(job.job_id);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };
  const statusTone = getStatusTone(job.status);

  const progress = job.progress || { total_files: 0, imported: 0, failed: 0, skipped: 0, bytes_transferred: 0 };
  const total = progress.total_files || 0;
  const imported = progress.imported || 0;
  const failed = progress.failed || 0;
  const skipped = progress.skipped || 0;

  const toPercent = (value: number): number => {
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, (value / total) * 100));
  };

  const importedWidth = toPercent(imported);
  const failedWidth = toPercent(failed);
  const skippedWidth = toPercent(skipped);

  const sourcePath = job.source_prefix || '/';
  const videoIds = [...(job.video_ids || []), ...outputPages.flatMap((page) => page.items.flatMap((part) => part.status === 'completed' && part.video_id ? [part.video_id] : []))];
  const failedFiles = [...(job.failed_files || []), ...failurePages.flatMap((page) => page.items.filter((file) => file.failure).map((file) => ({ path: file.file_id, file_id: file.file_id, file_name: file.file_name, error: file.failure!.error, ...file.failure })))];

  return (
    <article
      className={`job-card-compact ${isExpanded ? 'expanded' : ''}`}
      data-tone={statusTone}
      data-job-id={job.job_id}
    >
      <button className="job-card-compact-header" onClick={handleToggleExpand}>
        <div className="job-card-row">
          <div className="job-card-left">
            <span className="expand-icon">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <span className="job-icon">
              {isExpanded ? <FolderOpen size={14} /> : <Folder size={14} />}
            </span>
            <span className="job-name" title={sourcePath}>
              {sourcePath}
            </span>
          </div>

          <div className="job-progress-bar-container">
            {isInProgressStatus(job.status) ? (
              <ProcessingProgressBar status={job.status} phase={job.phase || job.status} lastWorkerActivityAt={job.liveness?.last_heartbeat_at}
                completed={imported + failed + skipped} total={total} failed={failed} unit="files" />
            ) : <div className="job-progress-bar">
              <div className="progress-segment success" style={{ width: `${importedWidth}%` }} />
              <div className="progress-segment failed" style={{ width: `${failedWidth}%` }} />
              <div className="progress-segment skipped" style={{ width: `${skippedWidth}%` }} />
            </div>}
          </div>

          <div className="job-card-right">
            <span className="job-connector" title={job.connector_id || 'local'}>{job.connector_id || 'local'}</span>
            <span className="job-time">{formatDateTime(job.created_at)}</span>
            <span className="job-counts">
              <span className="count-imported">{imported}</span>
              <span className="count-sep">/</span>
              <span className="count-failed">{failed}</span>
              <span className="count-sep">/</span>
              <span className="count-skipped">{skipped}</span>
            </span>
            <span className={`job-status-badge ${statusTone}`} title={job.status}>
              {job.status}
            </span>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="job-card-compact-details">
          {/* Row 1: Core identifiers */}
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Job ID</span>
              <span className="detail-value mono" title={job.job_id}>{job.job_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Connector ID</span>
              <span className="detail-value mono">{job.connector_id || 'local'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Target Index</span>
              <span className="detail-value mono">{job.target_index_id || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">{job.status}</span>
            </div>
          </div>

          {/* Row 2: Source configuration */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Source Path</span>
              <span className="detail-value mono" title={sourcePath}>{sourcePath}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">File Pattern</span>
              <span className="detail-value mono">{job.file_pattern || '*'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Recursive</span>
              <span className="detail-value">{job.recursive ? 'Yes' : 'No'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Data Transferred</span>
              <span className="detail-value">{formatBytes(progress.bytes_transferred || 0)}</span>
            </div>
          </div>

          {/* Row 3: Timestamps */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Created At</span>
              <span className="detail-value">{formatDateTimeFull(job.created_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Started At</span>
              <span className="detail-value">{formatDateTimeFull(job.started_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Completed At</span>
              <span className="detail-value">{formatDateTimeFull(job.completed_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Total Files</span>
              <span className="detail-value">{total}</span>
            </div>
          </div>

          {/* Row 4: File counts */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Imported</span>
              <span className="detail-value count-imported">{imported}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Failed</span>
              <span className="detail-value count-failed">{failed}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Skipped</span>
              <span className="detail-value count-skipped">{skipped}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Parts Ready</span>
              <span className="detail-value">{progress.parts_completed || 0} / {progress.parts_total || 0} ({progress.parts_failed || 0} failed)</span>
            </div>
          </div>

          <div className="detail-row-full"><span className="detail-label">Phase</span><span className="detail-value">{job.phase || job.status}</span></div>
          {job.execution?.status === 'blocked' && <div role="alert">Processing was not started: {job.execution.error}</div>}
          {/* Current file if processing */}
          {progress.current_file && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Current File</span>
              <span className="detail-value mono">{progress.current_file}</span>
            </div>
          )}

          {/* Video IDs */}
          {videoIds.length > 0 && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Created Video IDs ({videoIds.length})</span>
              <div className="detail-value mono id-list">
                {videoIds.map((id, idx) => (
                  <span key={idx} className="id-item">{id}</span>
                ))}
              </div>
            </div>
          )}

          {nextOutputCursor && <button type="button" disabled={loadingOutputs} onClick={loadMoreOutputs}>{loadingOutputs ? 'Loading…' : 'Load more parts'}</button>}
          {outputError && <div role="alert">{outputError}</div>}
          {/* Failed files */}
          {failedFiles.length > 0 && (
            <div className="detail-row-full error-list" style={{ marginTop: '10px' }}>
              <span className="detail-label">Failed Files ({failedFiles.length} of {failed})</span>
              <div className="detail-value mono file-list failed">
                {failedFiles.map((file, i) => (
                  <div key={file.file_id || file.path || i} className="file-item">
                    <div>{file.file_name || file.path || file.file_id}</div>
                    <div>{file.error}</div>
                    {file.http_status != null && <div>Download response: HTTP {file.http_status}</div>}
                    {file.refresh_required && (
                      <div>A fresh attachment is required. Reattach this file or upload it directly into the same index. Already imported files are available.</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {nextFailureCursor && <button type="button" disabled={loadingFailures} onClick={loadMoreFailures}>{loadingFailures ? 'Loading…' : 'Load more failed files'}</button>}
          {failureError && <div role="alert">{failureError}</div>}


          {/* Progress object as JSON */}
          <div className="detail-row-full" style={{ marginTop: '10px' }}>
            <span className="detail-label">Progress (Raw)</span>
            <pre className="raw-json">{JSON.stringify(progress, null, 2)}</pre>
          </div>

          {job.error_message && (
            <div className="error-message" title={job.error_message}>
              {job.error_message}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default ImportJobCardCompact;
